const requireMethod = (fn, expected, factory) => {
  if (typeof fn !== "function") {
    throw new TypeError(
      `${factory}() needs a ${expected} function, got ${typeof fn}. ` +
        "A gRPC/REST factory was most likely paired with the other one's method key.",
    );
  }
};

/**
 *
 * @param {Object} config
 * @param {Model} config.ModelRepo
 * @param {*} config.restListMethod
 * @param {string} config.insertMode
 * @param {(requestParameters: Object) => Object|falsy} config.handleRequestData
 * @param {(responseObject: Object) => *} config.handleResponseData
 * @param {(responseObject: Object) => *} config.onItemsInserted
 * @returns {({filters, pagination, requestData}: {filters: Object, pagination: Object, requestData: Object}) => Promise<*>}
 */
export const list = ({
  ModelRepo,
  restListMethod,
  insertMode = "save",
  handleRequestData = (requestParameters) => requestParameters,
  handleResponseData = (responseObject) => responseObject.results,
  onItemsInserted = (responseObject) => responseObject,
}) => {
  requireMethod(restListMethod, "restListMethod", "factoryRest.list");
  return async ({ filters = {}, pagination = {}, requestData = {}, additionalData = {} } = {}) => {
    const requestParameters = {
      ...filters,
      ...pagination,
      ...requestData,
    };

    const processedParams = handleRequestData(requestParameters, additionalData);
    if (!processedParams) return [];

    const response = await restListMethod(processedParams);
    const data = handleResponseData(response, additionalData);
    const items = await ModelRepo[insertMode](data);

    onItemsInserted(response, additionalData);

    return items || [];
  };
};

const MAX_PAGE_SIZE = 100;

/**
 *
 * @param {(params: Object) => Promise<{results: Array, count: number}>} restListMethod
 * @param {Object} params extra query params (filters) applied to every page
 * @returns {Promise<Array>}
 */
export const listAll = async (restListMethod, params = {}) => {
  const rows = [];
  let page = 1;

  for (;;) {
    const response = await restListMethod({
      ...params,
      page,
      pageSize: MAX_PAGE_SIZE,
    });
    const batch = response.results || [];
    rows.push(...batch);

    if (batch.length < MAX_PAGE_SIZE || rows.length >= (response.count ?? 0)) {
      return rows;
    }
    page += 1;
  }
};

/**
 *
 * @param {Object} config
 * @param {Model} config.ModelRepo
 * @param {*} config.restRetrieveMethod
 * @param {(itemPk: *) => Object} config.getRequestData
 * @param {(responseObject: Object) => *} config.handleResponseData
 * @returns {(itemPk: string) => Promise<*>}
 */
export const retrieve = ({
  ModelRepo,
  restRetrieveMethod,
  getRequestData = (itemPk) => ({ uuid: itemPk }),
  handleResponseData = (responseObject) => responseObject,
}) => {
  requireMethod(restRetrieveMethod, "restRetrieveMethod", "factoryRest.retrieve");
  return async (itemPk) => {
    const requestParameters = getRequestData(itemPk);
    const response = await restRetrieveMethod(requestParameters);
    const item = handleResponseData(response);

    return await ModelRepo.save(item);
  };
};
